/** Compact injection-metadata indicator — shows what was injected into the
 *  agent's context for the current turn. Tap to expand full details. */
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useState } from 'react';
import type { TurnMeta } from '@/lib/chatStore.types';
import { cn } from '@onestone/ui/utils';

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function formatChars(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

export function TurnMetaBar({ meta }: { meta: TurnMeta }) {
  const [expanded, setExpanded] = useState(false);

  const cachedSections = meta.systemPrompt.sections.filter((s) => s.cached).length;
  const totalSections = meta.systemPrompt.sections.length;

  return (
    <div className="border-t border-border/40 bg-muted/20">
      {/* Collapsed badge row — always visible */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-2xs text-muted-foreground transition-colors hover:bg-muted/40"
      >
        <Info className="h-3 w-3 shrink-0 opacity-60" />
        <span className="flex flex-wrap items-center gap-1.5">
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 font-medium',
              meta.executor === 'cli'
                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                : 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
            )}
          >
            {meta.executor.toUpperCase()}
          </span>
          <span className="opacity-50">·</span>
          <span>
            {formatTokens(meta.systemPrompt.totalTokens)} sys
            {cachedSections > 0 && (
              <span className="ml-1 text-green-600 dark:text-green-400">
                ({cachedSections}/{totalSections} cached)
              </span>
            )}
          </span>
          <span className="opacity-50">·</span>
          <span>{meta.context.messageCount} msgs</span>
          {meta.context.hasSummary && (
            <>
              <span className="opacity-50">·</span>
              <span className="text-amber-600 dark:text-amber-400">has checkpoint</span>
            </>
          )}
          {meta.context.nearlyFull && (
            <>
              <span className="opacity-50">·</span>
              <span className="text-orange-600 dark:text-orange-400">context nearly full — Summarize</span>
            </>
          )}
        </span>
        <span className="ml-auto">
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </span>
      </button>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="space-y-2 border-t border-border/30 px-3 py-2 text-2xs text-muted-foreground">
          {/* System prompt sections */}
          <div>
            <div className="mb-1 font-semibold uppercase tracking-wider opacity-60">System Prompt</div>
            <div className="space-y-0.5">
              {meta.systemPrompt.sections.map((s) => (
                <div key={s.name} className="flex items-center justify-between gap-2">
                  <span className="truncate">{s.name}</span>
                  <span className="shrink-0 tabular-nums">
                    {formatChars(s.chars)} chars · ~{formatTokens(s.tokens)} tok
                    {s.cached && <span className="ml-1 text-green-600 dark:text-green-400">✓cached</span>}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between border-t border-border/20 pt-1 font-medium">
              <span>Total</span>
              <span className="tabular-nums">
                {formatChars(meta.systemPrompt.totalChars)} chars · ~{formatTokens(meta.systemPrompt.totalTokens)} tok
              </span>
            </div>
          </div>

          {/* Token budget */}
          <div>
            <div className="mb-1 font-semibold uppercase tracking-wider opacity-60">Token Budget</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <span>Model window</span>
              <span className="text-right tabular-nums">{formatTokens(meta.budget.modelWindow)}</span>
              <span>Max output</span>
              <span className="text-right tabular-nums">{formatTokens(meta.budget.maxOutput)}</span>
              <span>System reserved</span>
              <span className="text-right tabular-nums">{formatTokens(meta.budget.systemReserved)}</span>
              <span>History budget</span>
              <span className="text-right tabular-nums">{formatTokens(meta.budget.historyBudget)}</span>
            </div>
          </div>

          {/* Context state */}
          <div>
            <div className="mb-1 font-semibold uppercase tracking-wider opacity-60">Context State</div>
            <div className="flex flex-wrap gap-2">
              <span>{meta.context.messageCount} messages in context</span>
              {meta.context.hasSummary && (
                <span className="rounded-sm bg-amber-500/10 px-1.5 py-0.5 text-amber-600 dark:text-amber-400">
                  Has checkpoint
                </span>
              )}
              {meta.context.nearlyFull && (
                <span className="rounded-sm bg-orange-500/10 px-1.5 py-0.5 text-orange-600 dark:text-orange-400">
                  Context nearly full — Summarize to continue
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
